import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileStack, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { track } from "@/lib/meta";

const field = "bg-zinc-900/70 border-zinc-800 text-sm";

export default function Templates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role !== "member";
  const [rows, setRows] = useState(null);
  const [clients, setClients] = useState([]);
  const [picked, setPicked] = useState(null);
  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newTpl, setNewTpl] = useState({ name: "", description: "", category: "Custom", placeholders: "", ai_instructions: "" });

  const load = () => api.get("/templates").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/proposals", {
        title: title || `${picked.name} proposal`,
        template_key: picked.key,
        client_id: clientId || null,
      });
      track("TemplateUsed", { content_ids: [picked.key], content_name: picked.name });
      track("ProposalCreated", { source: "template", content_ids: [data.id] });
      toast.success("Proposal created from template");
      navigate(`/editor/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveTemplate = async () => {
    try {
      await api.post("/templates", {
        ...newTpl,
        placeholders: newTpl.placeholders.split(",").map((s) => s.trim()).filter(Boolean),
        blocks: [],
      });
      setShowNew(false);
      setNewTpl({ name: "", description: "", category: "Custom", placeholders: "", ai_instructions: "" });
      load();
      toast.success("Template created");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeTemplate = async (key) => {
    try {
      await api.delete(`/templates/${key}`);
      load();
      toast.success("Template deleted");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell
      title="Templates"
      subtitle="Start from a proven structure instead of a blank page"
      actions={
        isAdmin && (
          <Button data-testid="new-template-btn" variant="outline" onClick={() => setShowNew(true)} className="h-9 border-zinc-800 text-zinc-300">
            New template
          </Button>
        )
      }
    >
      {!rows ? (
        <div className="h-40 grid place-items-center text-zinc-600">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {rows.map((t) => (
            <div key={t.key} data-testid={`template-card-${t.key}`} className="nv-card p-5 flex flex-col nv-rise">
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-lg bg-indigo-500/12 border border-indigo-500/25 grid place-items-center">
                  <FileStack className="w-4 h-4 text-indigo-400" />
                </div>
                {!t.is_starter && isAdmin && (
                  <button data-testid={`delete-template-${t.key}`} onClick={() => removeTemplate(t.key)} className="p-1.5 rounded hover:bg-red-500/15">
                    <Trash2 className="w-3.5 h-3.5 text-zinc-500" />
                  </button>
                )}
              </div>
              <p className="mt-3 font-semibold text-zinc-100">{t.name}</p>
              <p className="text-xs text-zinc-500 mt-1 flex-1">{t.description}</p>
              <div className="mt-3 flex items-center gap-2 text-[11px] font-num text-zinc-500">
                <span className="px-2 py-0.5 rounded-full border border-zinc-800">{t.category}</span>
                <span>{t.block_count} blocks</span>
              </div>
              <Button
                data-testid={`use-template-${t.key}`}
                onClick={() => {
                  setPicked(t);
                  setTitle(`${t.name} — `);
                }}
                className="mt-4 h-9 bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Use template
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!picked} onOpenChange={() => setPicked(null)}>
        <DialogContent className="bg-panel border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>{picked?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-500">Proposal title</label>
              <Input data-testid="template-title-input" className={`mt-1 ${field}`} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-zinc-500">Client</label>
              <select data-testid="template-client-select" className={`mt-1 h-9 w-full rounded-md px-2 ${field}`} value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">No client linked</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            {picked?.placeholders?.length > 0 && (
              <p className="text-xs text-zinc-500">
                Placeholders to fill in the editor: <span className="font-num text-indigo-300">{picked.placeholders.join(", ")}</span>
              </p>
            )}
            <Button data-testid="create-from-template-btn" onClick={create} disabled={busy} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create proposal"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-panel border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>New custom template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="tpl-name-input" className={field} placeholder="Template name" value={newTpl.name} onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })} />
            <Textarea data-testid="tpl-description-input" className={field} placeholder="When to use this" value={newTpl.description} onChange={(e) => setNewTpl({ ...newTpl, description: e.target.value })} />
            <Input data-testid="tpl-category-input" className={field} placeholder="Category" value={newTpl.category} onChange={(e) => setNewTpl({ ...newTpl, category: e.target.value })} />
            <Input data-testid="tpl-placeholders-input" className={field} placeholder="Placeholders, comma separated" value={newTpl.placeholders} onChange={(e) => setNewTpl({ ...newTpl, placeholders: e.target.value })} />
            <Textarea data-testid="tpl-ai-instructions-input" className={field} placeholder="AI instructions for this template" value={newTpl.ai_instructions} onChange={(e) => setNewTpl({ ...newTpl, ai_instructions: e.target.value })} />
            <Button data-testid="save-template-btn" onClick={saveTemplate} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              Save template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
